import React from "react";
import { motion } from "framer-motion";
import { Database, Search, Calculator, ArrowRight } from "lucide-react";
import PageMeta from "../site/PageMeta";
import { calendlyUrl, calendlyLinkProps } from "../../lib/calendly";

const services = [
  {
    icon: Database,
    title: "Data Processing & Analytics",
    body: "Structuring, cleaning, and analyzing data so it's actually usable — turned into reporting you can act on, not just a bigger spreadsheet.",
  },
  {
    icon: Search,
    title: "Market Research & Lead Intelligence",
    body: "Research and qualification work that gives your sales and strategy teams a real starting point instead of a cold list.",
  },
  {
    icon: Calculator,
    title: "Financial & Insurance Process Analytics",
    body: "Analysis support for financial and insurance workflows — underwriting data, claims trends, and reporting that needs domain knowledge to get right.",
  },
];

export default function KPO() {
  return (
    <>
      <PageMeta
        title="KPO — Knowledge Process Outsourcing | A4 Assist"
        description="Data and research work that requires actual judgment. Specialist-led analytics, market research, and financial analysis, supported by AI-assisted tools."
      />

      <section className="pt-36 pb-14">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#007AFF] mb-4">
            KPO — Knowledge Process Outsourcing
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] text-white leading-[1.05] max-w-4xl">
            Data and research work that requires{" "}
            <span className="font-serif italic">actual</span> judgment.
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
            This isn't data entry. Our KPO services are handled by specialists
            who understand the domain they're working in, supported by
            AI-assisted tools that make them faster and more consistent —
            not by tools trying to replace the judgment the work actually needs.
          </p>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-7 hover:border-[#007AFF]/50 transition-colors"
              data-testid={`kpo-service-${i}`}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/25">
                <s.icon size={20} />
              </div>
              <h3 className="font-display mt-5 text-lg font-semibold text-white">
                {s.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="pb-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-8">
          <div>
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">
              Sitting on data you haven't had time to use?
            </h3>
            <p className="mt-1.5 text-sm text-slate-400 max-w-lg">
              Tell us what you have and what you'd want to learn from it —
              we'll come back with an approach.
            </p>
          </div>
          <div className="flex gap-3">
            <a
              href={calendlyUrl({ pillar: "kpo" })}
              {...calendlyLinkProps}
              data-testid="kpo-contact-cta"
              className="group inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#050B14] font-semibold px-6 py-3.5 rounded-md shadow-[0_0_30px_-8px_rgba(0,229,255,0.6)]"
            >
              Get Free Audit <ArrowRight size={16} className="arrow-shift" />
            </a>
            <a
              href="mailto:sahil@a4assist.com?subject=A4%20Assist%20—%20KPO%20inquiry"
              className="inline-flex items-center gap-2 border border-slate-700 hover:border-[#00E5FF]/60 text-white font-semibold px-6 py-3.5 rounded-md"
            >
              Email Sahil
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
